import * as React from 'react'
import {ChangeEvent, Component, ReactElement, RefObject} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {
    ApplyButton,
    ItemHeading,
    ListGridContainer,
    WideWhiteArea,
} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {
    NatResidentHeavyLoadsCarryInfo,
    NatResidentHeavyLoadsCarryLocationState,
} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {HKConstants} from '../../../../../HKConstants'
import ResidentAttachmentFileArea, {
    FileInformation,
} from '../../../../../components/applications/ResidentAttachmentFileArea'
import {StyledSecondaryButton} from '../../../../../styledComponents/Button'
import {
    DisplayItemInputTitleNoMargin,
    DisplayItemInputTitleRequiredNoMargin,
} from '../../../../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {Colors} from '../../../../../styledComponents/Constants'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentHeavyLoadsCarryLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    heavyWeightGoodsDetail: Nullable<string>
    carryingStartDate: Nullable<string>
    carryingEndDate: Nullable<string>
    applyToAttachment: Nullable<FileInformation[]>

    submitButtonLabel: string
    onClickApplyConfirmButton: (
        applyRequest: NatResidentHeavyLoadsCarryInfo
    ) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    heavyWeightGoodsDetail: string
    carryingStartDate: string
    carryingEndDate: string
    applyToAttachment: FileInformation[]

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    applyFileAddDisable: boolean
    applyToAttachmentAddDisplay: boolean
    applyToAttachmentNoSelect: boolean
    isLoadCompleted: boolean
}

export default class NatResidentHeavyLoadsCarryForm extends Component<Props, State> {
    attachmentRef: RefObject<HTMLInputElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applicantFamilyName: '',
            applicantFirstName: '',
            heavyWeightGoodsDetail: '',
            carryingStartDate: '',
            carryingEndDate: '',
            applyToAttachment: [],
            currentDate: moment(),
            holidays: [],
            applyButtonDisabled: true,
            applyFileAddDisable: false,
            applyToAttachmentAddDisplay: true,
            applyToAttachmentNoSelect: false,
            isLoadCompleted: false
        }

        this.attachmentRef = React.createRef()

        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeCarryingStartDate = this.handleChangeCarryingStartDate.bind(this)
        this.handleChangeCarryingEndDate = this.handleChangeCarryingEndDate.bind(this)
        this.handleApplyToAttachmentInput = this.handleApplyToAttachmentInput.bind(this)
        this.handleApplyToAttachmentDelete = this.handleApplyToAttachmentDelete.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const heavyWeightGoodsDetail = this.props.heavyWeightGoodsDetail ?? this.state.heavyWeightGoodsDetail
            const carryingStartDate = this.props.carryingStartDate ?? this.state.carryingStartDate
            const carryingEndDate = this.props.carryingEndDate ?? this.state.carryingEndDate
            const applyToAttachment = this.props.applyToAttachment ?? this.state.applyToAttachment

            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                heavyWeightGoodsDetail: heavyWeightGoodsDetail,
                carryingStartDate: carryingStartDate,
                carryingEndDate: carryingEndDate,
                applyToAttachment: applyToAttachment,
                holidays: holidays === null ? [] : holidays
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
        }
        this.props.eventEmitter.closeLoading()
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value ? value : null}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeCarryingStartDate(date: string): void {
        this.setState({carryingStartDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeCarryingEndDate(date: string): void {
        this.setState({carryingEndDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleClickAddFileButton(elementId: string): void {
        const fileUpload = document.getElementById(elementId) as HTMLInputElement
        fileUpload.value = ''
        if (fileUpload) {
            fileUpload.click()
        }
    }

    handleApplyToAttachmentInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {applyToAttachment} = this.state
            const fileName = event.target.files[0].name
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                const fileData: string[] = String(reader.result).split(',')
                if (fileData[1]) {
                    applyToAttachment.push({
                        fileName: fileName,
                        fileData: fileData[1],
                        fileIndex: applyToAttachment.length,
                        fileNumber: null,
                        deleteFlag: false,
                    })
                    this.setState({
                        applyToAttachment: applyToAttachment,
                    }, () => {
                        this.updateApplyConfirmButtonDisabled()
                    })
                }
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.attachmentRef.current && this.attachmentRef.current.value) {
            this.attachmentRef.current.value = ''
        }
    }

    handleApplyToAttachmentDelete(file: FileInformation): void {
        const fileIndex = file.fileIndex ?? 0
        const applyToAttachmentList = this.state.applyToAttachment
        applyToAttachmentList[fileIndex] = file

        this.setState({
            applyToAttachment: applyToAttachmentList,
        }, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, heavyWeightGoodsDetail,
            carryingStartDate, carryingEndDate, applyToAttachment
        } = this.state

        let applyButtonDisabled = false
        const addFileCount = applyToAttachment.filter(file => !file.deleteFlag)

        if (!applicantFamilyName || !applicantFirstName || !heavyWeightGoodsDetail ||
            !carryingStartDate || !carryingEndDate || !(addFileCount.length >= 1)){
            applyButtonDisabled = true
        }
        this.setState({
            applyButtonDisabled: applyButtonDisabled,
            applyToAttachmentAddDisplay: !(addFileCount.length >= 20),
            applyToAttachmentNoSelect: (addFileCount.length === 0),
        })
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, heavyWeightGoodsDetail,
            carryingStartDate, carryingEndDate, applyToAttachment
        } = this.state


        const applyContent: NatResidentHeavyLoadsCarryInfo = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            heavyWeightGoodsDetail: heavyWeightGoodsDetail,
            carryingStartDate: carryingStartDate,
            carryingEndDate: carryingEndDate,
            applyToAttachment: applyToAttachment.filter(attachment => !attachment.deleteFlag)
        }

        if (!applyContent) {
            return
        }
        await this.props.onClickApplyConfirmButton(applyContent)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER)
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: applicantFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderHeavyWeightGoodsDetailForm(): ReactElement {
        const {heavyWeightGoodsDetail} = this.state

        return (
            <>
                <ItemHeading>
                    重量物
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'重量物の詳細'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 30,
                                    name: 'heavyWeightGoodsDetail',
                                    placeholder: '例：ピアノ',
                                    value: heavyWeightGoodsDetail
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['heavyWeightGoodsDetail']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderCarryingDateForm(): ReactElement {
        const {carryingStartDate, carryingEndDate, currentDate, holidays} = this.state
        const  maxOfStartDate = carryingEndDate ? new Date(carryingEndDate) : null
        const  minOfEndDate = carryingStartDate ? new Date(carryingStartDate) : null

        return (
            <>
                <ItemHeading>
                    重量物の搬入等期間
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'開始日'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={carryingStartDate}
                                onChange={this.handleChangeCarryingStartDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['carryingStartDate']}
                                eventEmitter={this.props.eventEmitter}
                                today={currentDate.toDate()}
                                maxDate={maxOfStartDate}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'終了日'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={carryingEndDate}
                                onChange={this.handleChangeCarryingEndDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['carryingEndDate']}
                                eventEmitter={this.props.eventEmitter}
                                minDate={minOfEndDate}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderApplyToAttachmentListInputForm(): ReactElement {
        const {applyToAttachment, applyToAttachmentAddDisplay, applyToAttachmentNoSelect} = this.state
        return (
            <>
                <ItemHeading>
                    添付資料
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    本申請に関連する下記資料はこちらから提出してください。
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={'16px'}>
                        <Box mb={'10px'}>・</Box>
                        <DisplayItemInputTitleRequiredNoMargin>
                            <Box component={'span'} color={Colors.Body}>物品証明書</Box>
                        </DisplayItemInputTitleRequiredNoMargin>
                        <Box mb={'10px'}>・</Box>
                        <DisplayItemInputTitleRequiredNoMargin>
                            <Box component={'span'} color={Colors.Body}>平面図</Box>
                        </DisplayItemInputTitleRequiredNoMargin>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleNoMargin>
                        添付資料を選択
                    </DisplayItemInputTitleNoMargin>
                    <Box height={'5px'}/>
                    <FileRemarkContainer fontSize={'12px'}>
                        <div>・</div>
                        <div>
                            20ファイルまで添付可能です。
                        </div>
                        <div>・</div>
                        <div>
                            ファイルは画像またはPDFでご提出ください。
                        </div>
                    </FileRemarkContainer>
                    <Box height={'10px'}/>
                    {applyToAttachmentAddDisplay &&
                        <StyledSecondaryButton
                            onClick={() => this.handleClickAddFileButton('fileUpload-attachmentFile')}>
                            {applyToAttachmentNoSelect ?
                                'ファイルを選択する' : 'ファイルを追加する'
                            }
                        </StyledSecondaryButton>
                    }
                    <DisplayNoneFileInput
                        type={'file'}
                        multiple
                        id={'fileUpload-attachmentFile'}
                        ref={this.attachmentRef}
                        onChange={this.handleApplyToAttachmentInput}
                    />
                    <Box height={'30px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'選択中の添付資料'}
                        component={
                            !applyToAttachmentNoSelect ?
                                applyToAttachment.map((attachmentFile, index) => (
                                    !applyToAttachment[index].deleteFlag &&
                                    <ResidentAttachmentFileArea
                                        key={index}
                                        fileInformation={applyToAttachment[index]}
                                        index={index}
                                        onDelete={this.handleApplyToAttachmentDelete}
                                        eventKeys={[`file[${index}]`]}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                )) : <StyledNoFile>選択されていません</StyledNoFile>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>)
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderHeavyWeightGoodsDetailForm()}
                < Box height={'15px'}/>
                {this.renderCarryingDateForm()}
                <Box height={'15px'}/>
                {this.renderApplyToAttachmentListInputForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const DisplayNoneFileInput = styled.input`
  display: none;
`
const StyledNoFile = styled.div`
  color: ${Colors.AccentLight};
  ${currentMedia.lessThan('sp')`
    margin-left: 18px;
  `};
`
const FileRemarkContainer = styled(ListGridContainer)`
  color: ${Colors.AccentDark};
`
